import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { RegimeIcmsEnum } from '@shared/enums/motor-fiscal/regime-icms.enum';
import { TabsScrollDirective } from '@shared/directives/tabs-scroll.directive';
import { HeaderComponent } from '@shared/components/header/header.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '@shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AnpService } from '@shared/services/apis/motor-fiscal/anp.service';
import { AnpLookupService } from '@shared/services/apis/motor-fiscal/anp-lookup.service';
import { UnidadeMedidaService } from '@shared/services/apis/produto/unidade-medida.service';
import { ProdutoService } from '@shared/services/apis/produto/produto.service';
import { ContarUsoAnpResponse } from '@shared/services/responses/produto/contar-uso-anp.response';
import { AnpCodigoStruct } from '@shared/services/structs/motor-fiscal/anp-codigo.struct';
import { AnpNcmStruct } from '@shared/services/structs/motor-fiscal/anp-ncm.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { map } from 'rxjs/operators';
import { PesquisaCodigoTributacaoModalComponent, PesquisaCodigoTributacaoModalData } from '@shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { AnpCodigoModalComponent } from './anp-codigo-modal/anp-codigo-modal.component';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-tabela-anp',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, RouterModule, MatTabsModule, MatTableModule, MatSortModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule, MatMenuModule,
    MatBadgeModule, MatProgressSpinnerModule, MatDialogModule,
    HeaderComponent, EmptyListComponent, LoadingBoxDefaultListComponent, AlertCardComponent,
    TabsScrollDirective, NgxMaskPipe,
  ],
  providers: [provideNgxMask()],
  templateUrl: './tabela-anp.component.html',
  styleUrls: ['./tabela-anp.component.css'],
})
export class TabelaAnpComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;
  public RegimeIcmsEnum = RegimeIcmsEnum;

  public abaAtivaIndex = 0;
  private readonly tamanhoPagina = 20;

  @ViewChild('sortCodigos') set sortCodigos(sort: MatSort) {
    if (sort) {
      this._dataSourceCodigos.sort = sort;
    }
  }
  @ViewChild('sortNcm') set sortNcm(sort: MatSort) {
    if (sort) {
      this._dataSourceNcm.sort = sort;
    }
  }

  // ── Códigos ANP ──
  public modelCodigos: FormGroup;
  public _isLoadingCodigos = false;
  public _dataSourceCodigos = new DataSourceOrdenacaoInsensivel<AnpCodigoStruct>();
  public _totalCodigos = 0;
  private _paginaCodigos = 1;
  public colunasCodigos = ['codigo', 'descricao', 'unidade', 'regime', 'aliquota', 'pbio', 'glp', 'status', 'actions'];

  // ── Mapeamento NCM ──
  public modelNcm: FormGroup;
  public _isLoadingNcm = false;
  public _dataSourceNcm = new DataSourceOrdenacaoInsensivel<AnpNcmStruct>();
  public _totalNcms = 0;
  private _paginaNcm = 1;
  private _ncmCarregado = false;
  public colunasNcm = ['codigoNcm', 'descricaoNcm', 'nivel', 'status', 'actions'];

  // ── Lookups ──
  public regimes: LookupItemStruct[] = [];
  public unidades: LookupItemStruct[] = [];
  public indicadores: LookupItemStruct[] = [];
  private unidadesMap = new Map<number, string>();

  constructor(
    private formBuilder: FormBuilder,
    private anpService: AnpService,
    private anpLookupService: AnpLookupService,
    private unidadeMedidaService: UnidadeMedidaService,
    private produtoService: ProdutoService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.modelCodigos = this.formBuilder.group({
      pesquisa: [''],
      idRegimeIcms: [null],
      exigePbio: [null],
      exigeGrupoGlp: [null],
      isAtivo: [null],
    });
    this.modelNcm = this.formBuilder.group({ pesquisa: [''] });

    this.carregarLookups();
    this.carregarCodigos(true);
  }

  public onTabChange(index: number): void {
    this.abaAtivaIndex = index;
    if (index === 1 && !this._ncmCarregado) {
      this.carregarNcms(true);
    }
  }

  private carregarLookups(): void {
    this.anpLookupService.getRegimeIcms().subscribe({ next: r => { if (!r.isError) this.regimes = r.list; } });
    this.anpLookupService.getIndicadorImportacao().subscribe({ next: r => { if (!r.isError) this.indicadores = r.list; } });

    this.unidadeMedidaService.getLookup().subscribe({
      next: r => {
        if (r.isError) return;
        this.unidades = r.list;
        this.unidadesMap = new Map(r.list.map(u => [u.id, u.descricao.split(' - ')[0]]));
      },
    });
  }

  public unidadeLabel(idUnidadeMedida: number): string {
    return this.unidadesMap.get(idUnidadeMedida) ?? '—';
  }

  // ── Códigos ──

  public get qtdFiltrosCodigos(): number {
    const m = this.modelCodigos?.value ?? {};
    return [m.idRegimeIcms, m.exigePbio, m.exigeGrupoGlp, m.isAtivo].filter(v => v !== null && v !== undefined).length;
  }

  public carregarCodigos(reset: boolean = false): void {
    if (reset) {
      this._paginaCodigos = 1;
      this._dataSourceCodigos.data = [];
    }
    this._isLoadingCodigos = true;
    const m = this.modelCodigos.value;
    this.anpService.getCodigos({
      pesquisa: m.pesquisa,
      idRegimeIcms: m.idRegimeIcms,
      exigePbio: m.exigePbio,
      exigeGrupoGlp: m.exigeGrupoGlp,
      isAtivo: m.isAtivo,
      paginaAtual: this._paginaCodigos,
      tamanhoPagina: this.tamanhoPagina,
    }).subscribe({
      next: response => {
        this._isLoadingCodigos = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this._totalCodigos = response.totalRegistros;
        this._dataSourceCodigos.data = reset ? response.itens : [...this._dataSourceCodigos.data, ...response.itens];
      },
      error: error => {
        this._isLoadingCodigos = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public clickPesquisarCodigos(): void { this.carregarCodigos(true); }
  public aplicarFiltrosCodigos(): void { this.carregarCodigos(true); }

  public limparFiltrosCodigos(): void {
    this.modelCodigos.patchValue({ idRegimeIcms: null, exigePbio: null, exigeGrupoGlp: null, isAtivo: null });
    this.carregarCodigos(true);
  }

  public proximaPaginaCodigos(): void {
    this._paginaCodigos++;
    this.carregarCodigos();
  }

  public abrirModalCodigo(codigo: AnpCodigoStruct | null): void {
    const ref = this.dialog.open(AnpCodigoModalComponent, {
      width: '720px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { codigo, regimes: this.regimes, unidades: this.unidades, indicadores: this.indicadores },
    });
    ref.afterClosed().subscribe(result => {
      if (result?.saved) this.carregarCodigos(true);
    });
  }

  public alternarStatusCodigo(codigo: AnpCodigoStruct): void {
    const novoStatus = !codigo.isAtivo;
    this.anpService.alterarStatusCodigo(codigo.idAnpEmpresa, novoStatus).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
          return;
        }
        codigo.isAtivo = novoStatus;
        this.alertService.show('Sucesso', `Código ANP ${novoStatus ? 'ativado' : 'inativado'}.`, AlertType.success);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public excluirCodigo(codigo: AnpCodigoStruct): void {
    // Antes de excluir, consulta na API Produto quantos produtos usam este código (cross-database).
    this.produtoService.contarUsoAnp(codigo.idAnpEmpresa).subscribe({
      next: (r: ContarUsoAnpResponse) => {
        const total = r.isError ? 0 : r.total;
        const descricao = total > 0
          ? `O código ANP "${codigo.codigo}" está vinculado a ${total} produto(s). A exclusão removerá o código e esses produtos perderão o vínculo. Deseja continuar?`
          : `O código ANP "${codigo.codigo}" será removido. Esta ação não pode ser desfeita.`;
        this.confirmarExclusaoCodigo(codigo, descricao);
      },
      // Falha ao contar não bloqueia a exclusão: segue com a mensagem padrão.
      error: () => this.confirmarExclusaoCodigo(codigo, `O código ANP "${codigo.codigo}" será removido. Esta ação não pode ser desfeita.`),
    });
  }

  private confirmarExclusaoCodigo(codigo: AnpCodigoStruct, descricao: string): void {
    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir Código ANP?',
        description: descricao,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });
    ref.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.anpService.excluirCodigo(codigo.idAnpEmpresa).subscribe({
        next: response => {
          if (response.isError) { this.alertService.show('Erro', response.errorDescription, AlertType.error); return; }
          this.alertService.show('Sucesso', 'Código ANP excluído.', AlertType.success);
          this.carregarCodigos(true);
        },
        error: error => this.alertService.show('Erro inesperado', error.message, AlertType.error),
      });
    });
  }

  // ── NCM ──

  public carregarNcms(reset: boolean = false): void {
    if (reset) {
      this._paginaNcm = 1;
      this._dataSourceNcm.data = [];
    }
    this._ncmCarregado = true;
    this._isLoadingNcm = true;
    this.anpService.getNcms(this.modelNcm.get('pesquisa')?.value || '', null, this._paginaNcm, this.tamanhoPagina).subscribe({
      next: response => {
        this._isLoadingNcm = false;
        if (response.isError) { this.alertService.show('Erro', response.errorDescription, AlertType.error); return; }
        this._totalNcms = response.totalRegistros;
        this._dataSourceNcm.data = reset ? response.itens : [...this._dataSourceNcm.data, ...response.itens];
      },
      error: error => {
        this._isLoadingNcm = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public clickPesquisarNcm(): void { this.carregarNcms(true); }

  public proximaPaginaNcm(): void {
    this._paginaNcm++;
    this.carregarNcms();
  }

  public abrirModalNcm(): void {
    const ref = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '80vh',
      data: {
        titulo: 'Adicionar Mapeamento NCM',
        buscarFn: (pesquisa: string) =>
          this.anpLookupService.getNcmLookup(pesquisa).pipe(
            map(r => ({ isError: r.isError, itens: (r.list ?? []).map(i => ({ codigo: i.codigo ?? '', descricao: i.descricao })) }))
          ),
        permitirManual: false,
        registrosIniciais: 20,
      } as PesquisaCodigoTributacaoModalData,
    });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      const codigoBruto = result.manual ? result.codigoManual : result.selecionado?.codigo;
      const codigoNcm = (codigoBruto ?? '').replace(/\D/g, '');
      const descricaoNcm = result.manual ? (result.descricaoManual ?? '') : (result.selecionado?.descricao ?? '');
      if (!codigoNcm) return;
      this.anpService.criarNcm({ codigoNcm, descricaoNcm, isAtivo: true }).subscribe({
        next: response => {
          if (response.isError) { this.alertService.show('Erro', response.errorDescription, AlertType.error); return; }
          this.alertService.show('Sucesso', 'Mapeamento NCM adicionado.', AlertType.success);
          this.carregarNcms(true);
        },
        error: err => this.alertService.show('Erro inesperado', err.message, AlertType.error),
      });
    });
  }

  public alternarStatusNcm(ncm: AnpNcmStruct): void {
    const novoStatus = !ncm.isAtivo;
    this.anpService.alterarStatusNcm(ncm.idAnpEmpresaNcm, novoStatus).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
          return;
        }
        ncm.isAtivo = novoStatus;
        this.alertService.show('Sucesso', `Mapeamento NCM x ANP ${novoStatus ? 'ativado' : 'inativado'}.`, AlertType.success);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public excluirNcm(ncm: AnpNcmStruct): void {
    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir Mapeamento NCM?',
        description: `O mapeamento NCM "${ncm.codigoNcm}" será removido. Esta ação não pode ser desfeita.`,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });
    ref.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.anpService.excluirNcm(ncm.idAnpEmpresaNcm).subscribe({
        next: response => {
          if (response.isError) { this.alertService.show('Erro', response.errorDescription, AlertType.error); return; }
          this.alertService.show('Sucesso', 'Mapeamento excluído.', AlertType.success);
          this.carregarNcms(true);
        },
        error: error => this.alertService.show('Erro inesperado', error.message, AlertType.error),
      });
    });
  }
}
